import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import firebaseAuthService from '../services/firebase/FirebaseAuthService';
import { useAuth } from '../contexts/AuthContext';
import { validatePassword, getStrengthColor } from '../utils/passwordValidation';
import { doc, getDoc, setDoc, serverTimestamp, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { signInWithEmailAndPassword, updatePassword, updateProfile } from 'firebase/auth';
import { db, auth } from '../config/firebase';
import { UserRole } from '../types/roles';

/** Map human-readable invite role to UserRole enum or custom role ID */
function mapInviteRole(inviteRole: string): string {
  // Check known default roles first
  switch (inviteRole) {
    case 'System Administrator': return 'system_admin';
    case 'Clinical Director': return 'clinical_director';
    case 'Administrative Staff': return 'administrative_staff';
    case 'Provider (Clinician)': return 'provider';
  }
  // Custom roles: normalize name to ID format (e.g., "HR Manager" → "hr_manager")
  if (inviteRole) {
    return inviteRole.toLowerCase().replace(/[^a-z0-9]+/g, '_');
  }
  return 'provider';
}

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN',
  'IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH',
  'NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT',
  'VT','VA','WA','WV','WI','WY'
];

const LICENSE_TYPES = [
  { value: 'LCSW', label: 'LCSW - Licensed Clinical Social Worker' },
  { value: 'LPC', label: 'LPC - Licensed Professional Counselor' },
  { value: 'LMFT', label: 'LMFT - Licensed Marriage & Family Therapist' },
  { value: 'PsyD', label: 'PsyD - Doctor of Psychology' },
  { value: 'PhD', label: 'PhD - Doctor of Philosophy (Psychology)' },
  { value: 'MD', label: 'MD - Doctor of Medicine (Psychiatry)' },
  { value: 'DO', label: 'DO - Doctor of Osteopathic Medicine' },
  { value: 'LMHC', label: 'LMHC - Licensed Mental Health Counselor' },
  { value: 'LCPC', label: 'LCPC - Licensed Clinical Professional Counselor' },
  { value: 'LPCC', label: 'LPCC - Licensed Professional Clinical Counselor' },
  { value: 'NP', label: 'NP - Nurse Practitioner (Psychiatric)' },
  { value: 'Other', label: 'Other' },
];

const SPECIALTIES = [
  'Anxiety',
  'Depression',
  'Trauma / PTSD',
  'Substance Abuse',
  'Couples Therapy',
  'Family Therapy',
  'Child & Adolescent',
  'Eating Disorders',
  'OCD',
  'ADHD',
  'Grief & Loss',
  'Anger Management',
  'Career Counseling',
  'Chronic Pain',
  'Sleep Disorders',
];

interface StateLicense {
  state: string;
  licenseNumber: string;
}

interface SignupForm {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  licenseType: string;
  npiNumber: string;
  licenses: StateLicense[];
  specialties: string[];
  practiceName: string;
}

const ProviderSignup: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isProvider, refreshProfile } = useAuth();
  const isInvite = searchParams.get('invite') === 'true';
  // Display only — the authoritative role lives on the server-side invite
  // record keyed by this code, and is applied by /api/redeem-invite.
  const inviteRole = searchParams.get('role') || '';
  const inviteCode = (searchParams.get('code') || window.localStorage.getItem('inviteCode') || '').trim();

  // config/app.registrationOpen gates self-serve signup. Starts false and
  // stays false if the document is missing or unreadable — a config lookup
  // that fails must not open registration.
  const [registrationOpen, setRegistrationOpen] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'config', 'app'));
        if (!cancelled) setRegistrationOpen(snap.exists() && snap.data().registrationOpen === true);
      } catch {
        // Fail closed.
      }
    })();
    return () => { cancelled = true; };
  }, []);
  const invitePracticeName = searchParams.get('practiceName') || '';
  const isClinicalRole = !inviteRole || ['Provider (Clinician)', 'Clinical Director', 'System Administrator'].includes(inviteRole);
  const userRole = isInvite ? mapInviteRole(inviteRole) : 'provider';

  const [form, setForm] = useState<SignupForm>({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    licenseType: '',
    npiNumber: '',
    licenses: [{ state: '', licenseNumber: '' }],
    specialties: [],
    practiceName: invitePracticeName,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState(1);

  // If arriving from invite link, sign out any current user once so the invited person gets a clean signup
  const [inviteSignOutDone, setInviteSignOutDone] = useState(false);
  useEffect(() => {
    if (isInvite && isAuthenticated && !inviteSignOutDone && !isLoading) {
      setInviteSignOutDone(true);
      firebaseAuthService.logout();
    }
  }, [isInvite, isAuthenticated, inviteSignOutDone, isLoading]);

  // Pre-fill form from invite link params
  useEffect(() => {
    const inviteEmail = searchParams.get('email');
    const inviteName = searchParams.get('name');
    if (isInvite && (inviteEmail || inviteName)) {
      setForm(prev => ({
        ...prev,
        email: inviteEmail || prev.email,
        firstName: inviteName?.split(' ')[0] || prev.firstName,
        lastName: inviteName?.split(' ').slice(1).join(' ') || prev.lastName,
      }));
    }
  }, [isInvite, searchParams]);

  // Redirect if already logged in as provider (skip if invite link)
  useEffect(() => {
    if (!isInvite && isAuthenticated && isProvider) {
      navigate('/provider-dashboard', { replace: true });
    }
  }, [isInvite, isAuthenticated, isProvider, navigate]);

  const passwordValidation = validatePassword(form.password);

  const handleChange = (field: keyof SignupForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  const toggleSpecialty = (specialty: string) => {
    setForm((prev) => ({
      ...prev,
      specialties: prev.specialties.includes(specialty)
        ? prev.specialties.filter((s) => s !== specialty)
        : [...prev.specialties, specialty],
    }));
  };

  const validateStep1 = (): boolean => {
    if (!form.firstName.trim()) { setError('First name is required'); return false; }
    if (!form.lastName.trim()) { setError('Last name is required'); return false; }
    if (!form.email.trim()) { setError('Email is required'); return false; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { setError('Please enter a valid email address'); return false; }
    if (!passwordValidation.isValid) { setError('Password does not meet all requirements'); return false; }
    if (form.password !== form.confirmPassword) { setError('Passwords do not match'); return false; }
    return true;
  };

  const validateStep2 = (): boolean => {
    // Non-clinical staff (Administrative Staff) don't need credentials
    if (!isClinicalRole) return true;
    if (!form.licenseType) { setError('License type is required'); return false; }
    if (!form.npiNumber.trim()) { setError('NPI number is required'); return false; }
    if (!/^\d{10}$/.test(form.npiNumber.trim())) { setError('NPI must be exactly 10 digits'); return false; }
    for (let i = 0; i < form.licenses.length; i++) {
      const lic = form.licenses[i];
      if (!lic.state) { setError(`State is required for license ${i + 1}`); return false; }
      if (!lic.licenseNumber.trim()) { setError(`License number is required for ${lic.state}`); return false; }
    }
    if (form.specialties.length === 0) { setError('Select at least one specialty'); return false; }
    return true;
  };

  const addLicense = () => {
    setForm(prev => ({
      ...prev,
      licenses: [...prev.licenses, { state: '', licenseNumber: '' }]
    }));
  };

  const removeLicense = (index: number) => {
    if (form.licenses.length <= 1) return;
    setForm(prev => ({
      ...prev,
      licenses: prev.licenses.filter((_, i) => i !== index)
    }));
  };

  const updateLicense = (index: number, field: keyof StateLicense, value: string) => {
    setForm(prev => ({
      ...prev,
      licenses: prev.licenses.map((lic, i) => i === index ? { ...lic, [field]: value } : lic)
    }));
    if (error) setError('');
  };

  const handleNext = () => {
    setError('');
    if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
    }
  };

  const handleBack = () => {
    setError('');
    setCurrentStep(1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateStep2()) return;

    setIsLoading(true);

    try {
      const displayName = `${form.firstName.trim()} ${form.lastName.trim()}`;
      let uid: string;

      if (isInvite) {
        // Invite flow: Firebase email link may have already created an auth account.
        // Try to create new account first; if email exists, sign in and set password.
        //
        // The Firestore profile is NOT written here. /api/redeem-invite writes
        // it with the Admin SDK, taking the roles from the server-side invite
        // record — the role in this URL is display only, and a recipient can
        // edit it.
        let credential;
        try {
          credential = await firebaseAuthService.registerProvider(
            form.email.trim(),
            form.password,
            displayName,
            undefined, // clinicId
            userRole as any,
            true // skipProfile — the server writes it on redeem
          );
        } catch (regErr: any) {
          if (regErr.message?.includes('already exists') || regErr.code === 'auth/email-already-in-use') {
            // Account was created by email link — sign in with the link, then set password
            const emailForSignIn = form.email.trim();
            credential = await firebaseAuthService.signInWithEmailLink(emailForSignIn, window.location.href);
            await updatePassword(credential.user, form.password);
            await updateProfile(credential.user, { displayName });
          } else {
            throw regErr;
          }
        }
        uid = credential.user.uid;

        // Redeem only after the account exists, so a failed signup leaves the
        // invite reusable.
        const token = await credential.user.getIdToken(true);
        const redeemRes = await fetch('/api/redeem-invite', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ code: inviteCode }),
        });

        if (!redeemRes.ok) {
          const detail = await redeemRes.json().catch(() => ({}));
          throw new Error(
            detail.error || 'Your account was created but the invitation could not be completed. Ask for a new invite.'
          );
        }

        // Pick up the roles the server just granted.
        await credential.user.getIdToken(true);
      } else {
        // Normal signup flow
        const userCredential = await firebaseAuthService.registerProvider(
          form.email.trim(),
          form.password,
          displayName
        );
        uid = userCredential.user.uid;
      }

      // Create detailed provider profile in Firestore providers collection (clinical roles only)
      if (isClinicalRole) {
        await setDoc(doc(db, 'providers', uid), {
          uid,
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          licenseType: form.licenseType,
          npiNumber: form.npiNumber.trim(),
          licenses: form.licenses.map(l => ({ state: l.state, licenseNumber: l.licenseNumber.trim() })),
          // Keep flat fields for backward compatibility (primary state)
          licenseNumber: form.licenses[0]?.licenseNumber.trim() || '',
          stateOfLicensure: form.licenses[0]?.state || '',
          specialties: form.specialties,
          practiceName: form.practiceName.trim() || invitePracticeName || null,
          isActive: true,
          isCredentialed: false,
          createdAt: serverTimestamp(),
        });
      }

      // Refresh AuthContext so ProtectedRoute sees the new profile
      await refreshProfile();

      // Update team member status from 'invited' to 'active' (direct Firestore query)
      if (isInvite) {
        try {
          const q = query(collection(db, 'team_members'), where('email', '==', form.email.trim().toLowerCase()));
          const snap = await getDocs(q);
          snap.forEach(async (d) => {
            await updateDoc(doc(db, 'team_members', d.id), {
              status: 'active',
              authUid: uid, // Link Firebase Auth UID to team member
              lastSignIn: new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }),
            });
          });
        } catch (e) { console.warn('Team member status update skipped:', e); }
      }

      navigate('/provider-dashboard');
    } catch (err: any) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const strengthColor = getStrengthColor(passwordValidation.score);

  // Self-serve registration is closed — accounts are created by invitation,
  // and the profile is written server-side by /api/redeem-invite. The
  // authoritative block is the Firestore rule (users/{uid} create: false);
  // this is an honest screen instead of a permission error.
  if (!isInvite && !registrationOpen) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0A2540',
        padding: '2rem',
      }}>
        <div style={{
          maxWidth: '460px',
          textAlign: 'center',
          background: '#16325B',
          border: '1px solid #28527A',
          borderRadius: '12px',
          padding: '2.5rem',
          color: '#f9fafb',
        }}>
          <h1 style={{ fontSize: '1.4rem', margin: '0 0 0.75rem' }}>Registration is by invitation</h1>
          <p style={{ color: '#d1d5db', fontSize: '0.95rem', lineHeight: 1.6, margin: '0 0 1.5rem' }}>
            MyClinicCore accounts are created by your practice administrator. If
            you're joining a practice, use the invitation link sent to your email
            address.
          </p>
          <Link to="/provider-login" style={{ color: '#6CA6C1', textDecoration: 'none', fontSize: '0.95rem' }}>
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <Header />

      <main id="main-content" className="page">
        <div className="container" style={{ paddingBottom: '2rem' }}>
          <div style={{
            maxWidth: '480px',
            margin: '0 auto',
            paddingTop: '2rem'
          }}>
            {/* Header */}
            <div className="text-center" style={{ marginBottom: '2rem' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem', textAlign: 'center' }}>
                My ClinicCore
              </div>
              <h1 className="page-title" style={{ marginBottom: '0.5rem' }}>
                Provider Registration
              </h1>
              <p style={{ color: '#d1d5db' }}>
                {currentStep === 1
                  ? 'Create your account to get started'
                  : 'Enter your professional credentials'
                }
              </p>

              {/* Step Indicator */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1rem' }}>
                <div style={{
                  width: '2rem', height: '4px', borderRadius: '2px',
                  backgroundColor: '#D4A017'
                }} />
                <div style={{
                  width: '2rem', height: '4px', borderRadius: '2px',
                  backgroundColor: currentStep >= 2 ? '#D4A017' : '#374151'
                }} />
              </div>
            </div>

            <div className="card">
              <form onSubmit={handleSubmit}>
                {currentStep === 1 ? (
                  /* ─── Step 1: Account Details ─── */
                  <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
                    <legend style={{
                      color: '#D4A017', fontSize: '1rem', fontWeight: '600',
                      marginBottom: '1rem', padding: 0
                    }}>
                      Account Information
                    </legend>

                    {/* Name Row */}
                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                      <div className="form-group" style={{ flex: 1 }}>
                        <label htmlFor="signup-first-name" className="form-label">First Name</label>
                        <input
                          id="signup-first-name"
                          type="text"
                          className="form-input"
                          placeholder="First name"
                          value={form.firstName}
                          onChange={(e) => handleChange('firstName', e.target.value)}
                          disabled={isLoading}
                          required
                          aria-required="true"
                          aria-invalid={error ? 'true' : undefined}
                          aria-describedby={error ? 'signup-step1-error' : undefined}
                        />
                      </div>
                      <div className="form-group" style={{ flex: 1 }}>
                        <label htmlFor="signup-last-name" className="form-label">Last Name</label>
                        <input
                          id="signup-last-name"
                          type="text"
                          className="form-input"
                          placeholder="Last name"
                          value={form.lastName}
                          onChange={(e) => handleChange('lastName', e.target.value)}
                          disabled={isLoading}
                          required
                          aria-required="true"
                          aria-invalid={error ? 'true' : undefined}
                          aria-describedby={error ? 'signup-step1-error' : undefined}
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div className="form-group">
                      <label htmlFor="signup-email" className="form-label">Email Address</label>
                      <input
                        id="signup-email"
                        type="email"
                        className="form-input"
                        placeholder="you@practice.com"
                        value={form.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        disabled={isLoading}
                        required
                        aria-required="true"
                        aria-invalid={error ? 'true' : undefined}
                        aria-describedby={error ? 'signup-step1-error' : undefined}
                        autoComplete="email"
                      />
                    </div>

                    {/* Password */}
                    <div className="form-group">
                      <label htmlFor="signup-password" className="form-label">Password</label>
                      <div style={{ position: 'relative' }}>
                        <input
                          id="signup-password"
                          type={showPassword ? 'text' : 'password'}
                          className="form-input"
                          placeholder="Create a strong password"
                          value={form.password}
                          onChange={(e) => handleChange('password', e.target.value)}
                          disabled={isLoading}
                          required
                          aria-required="true"
                          aria-describedby="password-strength password-checks"
                          autoComplete="new-password"
                          style={{ paddingRight: '3rem' }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          style={{
                            position: 'absolute', right: '0.75rem', top: '50%',
                            transform: 'translateY(-50%)', background: 'none',
                            border: 'none', color: '#d1d5db', cursor: 'pointer',
                            fontSize: '0.875rem'
                          }}
                          disabled={isLoading}
                        >
                          {showPassword ? 'Hide' : 'Show'}
                        </button>
                      </div>

                      {/* Password Strength Meter */}
                      {form.password && (
                        <div style={{ marginTop: '0.5rem' }}>
                          <div
                            id="password-strength"
                            aria-live="polite"
                            style={{
                              display: 'flex', alignItems: 'center', gap: '0.5rem',
                              marginBottom: '0.25rem'
                            }}
                          >
                            <div style={{
                              flex: 1, height: '4px', backgroundColor: '#374151',
                              borderRadius: '2px', overflow: 'hidden'
                            }}>
                              <div
                                style={{
                                  width: `${(passwordValidation.score / 5) * 100}%`,
                                  height: '100%',
                                  backgroundColor: strengthColor,
                                  transition: 'width 0.3s, background-color 0.3s',
                                }}
                                role="progressbar"
                                aria-valuenow={passwordValidation.score}
                                aria-valuemin={0}
                                aria-valuemax={5}
                                aria-label={`Password strength: ${passwordValidation.strengthLabel}`}
                              />
                            </div>
                            <span style={{ fontSize: '0.75rem', color: strengthColor, minWidth: '70px' }}>
                              {passwordValidation.strengthLabel}
                            </span>
                          </div>

                          <ul id="password-checks" style={{
                            listStyle: 'none', padding: 0, margin: 0,
                            fontSize: '0.75rem'
                          }}>
                            {passwordValidation.checks.map((check, i) => (
                              <li key={i} style={{
                                color: check.met ? '#22c55e' : '#6b7280',
                                display: 'flex', alignItems: 'center', gap: '0.25rem',
                                marginBottom: '0.125rem'
                              }}>
                                <span aria-hidden="true">{check.met ? '\u2713' : '\u2717'}</span>
                                <span>{check.label}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Confirm Password */}
                    <div className="form-group">
                      <label htmlFor="signup-confirm-password" className="form-label">Confirm Password</label>
                      <div style={{ position: 'relative' }}>
                        <input
                          id="signup-confirm-password"
                          type={showConfirm ? 'text' : 'password'}
                          className="form-input"
                          placeholder="Confirm your password"
                          value={form.confirmPassword}
                          onChange={(e) => handleChange('confirmPassword', e.target.value)}
                          disabled={isLoading}
                          required
                          aria-required="true"
                          aria-describedby={form.confirmPassword && form.password !== form.confirmPassword ? 'confirm-mismatch' : undefined}
                          autoComplete="new-password"
                          style={{ paddingRight: '3rem' }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirm(!showConfirm)}
                          aria-label={showConfirm ? 'Hide password confirmation' : 'Show password confirmation'}
                          style={{
                            position: 'absolute', right: '0.75rem', top: '50%',
                            transform: 'translateY(-50%)', background: 'none',
                            border: 'none', color: '#d1d5db', cursor: 'pointer',
                            fontSize: '0.875rem'
                          }}
                          disabled={isLoading}
                        >
                          {showConfirm ? 'Hide' : 'Show'}
                        </button>
                      </div>
                      {form.confirmPassword && form.password !== form.confirmPassword && (
                        <p id="confirm-mismatch" style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                          Passwords do not match
                        </p>
                      )}
                    </div>

                    {/* Error */}
                    {error && (
                      <div id="signup-step1-error" className="form-error" role="alert" aria-live="assertive" style={{ marginBottom: '1rem' }}>
                        {error}
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={handleNext}
                      className="btn btn-primary"
                      style={{ width: '100%' }}
                      disabled={isLoading}
                    >
                      {isClinicalRole ? 'Next: Professional Credentials' : 'Next: Finish Setup'}
                    </button>
                  </fieldset>
                ) : (
                  /* ─── Step 2: Professional Credentials ─── */
                  <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
                    <legend style={{
                      color: '#D4A017', fontSize: '1rem', fontWeight: '600',
                      marginBottom: '1rem', padding: 0
                    }}>
                      {isClinicalRole ? 'Professional Credentials' : 'Account Setup'}
                    </legend>

                    {isClinicalRole && (<>
                    {/* License Type */}
                    <div className="form-group">
                      <label htmlFor="signup-license-type" className="form-label">License Type</label>
                      <select
                        id="signup-license-type"
                        className="form-input"
                        value={form.licenseType}
                        onChange={(e) => handleChange('licenseType', e.target.value)}
                        disabled={isLoading}
                        required
                        aria-required="true"
                        aria-invalid={error ? 'true' : undefined}
                        aria-describedby={error ? 'signup-step2-error' : undefined}
                      >
                        <option value="">Select license type</option>
                        {LICENSE_TYPES.map((lt) => (
                          <option key={lt.value} value={lt.value}>{lt.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* NPI Number */}
                    <div className="form-group">
                      <label htmlFor="signup-npi" className="form-label">NPI Number</label>
                      <input
                        id="signup-npi"
                        type="text"
                        className="form-input"
                        placeholder="10-digit NPI"
                        value={form.npiNumber}
                        onChange={(e) => handleChange('npiNumber', e.target.value.replace(/\D/g, '').slice(0, 10))}
                        maxLength={10}
                        disabled={isLoading}
                        required
                        aria-required="true"
                        aria-describedby={error ? 'npi-help signup-step2-error' : 'npi-help'}
                        inputMode="numeric"
                      />
                      <p id="npi-help" style={{ color: '#d1d5db', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                        Find your NPI at nppes.cms.hhs.gov
                      </p>
                    </div>

                    {/* State Licenses */}
                    <div className="form-group">
                      <label className="form-label">State Licenses</label>
                      {form.licenses.map((lic, idx) => (
                        <div key={idx} style={{
                          display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem'
                        }}>
                          <select
                            className="form-input"
                            value={lic.state}
                            onChange={(e) => updateLicense(idx, 'state', e.target.value)}
                            disabled={isLoading}
                            style={{ flex: '0 0 90px' }}
                            aria-label={`State for license ${idx + 1}`}
                          >
                            <option value="">State</option>
                            {US_STATES.map((st) => (
                              <option key={st} value={st}>{st}</option>
                            ))}
                          </select>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="License number"
                            value={lic.licenseNumber}
                            onChange={(e) => updateLicense(idx, 'licenseNumber', e.target.value)}
                            disabled={isLoading}
                            style={{ flex: 1 }}
                            aria-label={`License number for ${lic.state || 'state ' + (idx + 1)}`}
                          />
                          {form.licenses.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeLicense(idx)}
                              style={{
                                background: 'transparent', border: 'none', color: '#ef4444',
                                cursor: 'pointer', fontSize: '1.25rem', padding: '0 0.25rem',
                                lineHeight: 1
                              }}
                              aria-label={`Remove license for ${lic.state || 'state ' + (idx + 1)}`}
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addLicense}
                        disabled={isLoading}
                        style={{
                          background: 'transparent', border: '1px dashed #4b5563',
                          color: '#6CA6C1', padding: '0.5rem 1rem', borderRadius: '0.375rem',
                          cursor: 'pointer', fontSize: '0.875rem', width: '100%', marginTop: '0.25rem'
                        }}
                      >
                        + Add Another State
                      </button>
                    </div>

                    {/* Specialties — multi-select */}
                    <div className="form-group">
                      <label className="form-label" id="specialties-label">
                        Specialties <span style={{ color: '#d1d5db', fontWeight: '400' }}>(select all that apply)</span>
                      </label>
                      <div
                        role="group"
                        aria-labelledby="specialties-label"
                        style={{
                          display: 'grid', gridTemplateColumns: '1fr 1fr',
                          gap: '0.5rem',
                          maxHeight: '240px', overflowY: 'auto',
                          padding: '0.75rem', borderRadius: '0.375rem',
                          border: '1px solid #374151', backgroundColor: '#111827'
                        }}
                      >
                        {SPECIALTIES.map((specialty) => {
                          const isSelected = form.specialties.includes(specialty);
                          return (
                            <label
                              key={specialty}
                              style={{
                                display: 'flex', alignItems: 'center', gap: '0.5rem',
                                padding: '0.375rem 0.5rem', borderRadius: '0.375rem',
                                fontSize: '0.8rem', cursor: 'pointer',
                                backgroundColor: isSelected ? 'rgba(212, 160, 23, 0.15)' : 'transparent',
                                color: isSelected ? '#D4A017' : '#d1d5db',
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleSpecialty(specialty)}
                                disabled={isLoading}
                                style={{ accentColor: '#D4A017', width: '16px', height: '16px', flexShrink: 0 }}
                              />
                              {specialty}
                            </label>
                          );
                        })}
                      </div>
                      {form.specialties.length > 0 && (
                        <p style={{ color: '#d1d5db', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                          {form.specialties.length} selected
                        </p>
                      )}
                    </div>
                    </>)}

                    {/* Practice Name (optional) */}
                    <div className="form-group">
                      <label htmlFor="signup-practice" className="form-label">
                        Practice Name <span style={{ color: '#d1d5db', fontWeight: '400' }}>(optional)</span>
                      </label>
                      <input
                        id="signup-practice"
                        type="text"
                        className="form-input"
                        placeholder="e.g., Sunrise Therapy Group"
                        value={form.practiceName}
                        onChange={(e) => handleChange('practiceName', e.target.value)}
                        disabled={isLoading}
                      />
                    </div>

                    {/* Error */}
                    {error && (
                      <div id="signup-step2-error" className="form-error" role="alert" aria-live="assertive" style={{ marginBottom: '1rem' }}>
                        {error}
                      </div>
                    )}

                    {/* Buttons */}
                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                      <button
                        type="button"
                        onClick={handleBack}
                        style={{
                          flex: 1, padding: '0.75rem',
                          backgroundColor: '#374151', color: '#d1d5db',
                          border: '1px solid #4b5563', borderRadius: '0.375rem',
                          cursor: 'pointer', fontSize: '0.875rem'
                        }}
                        disabled={isLoading}
                      >
                        Back
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        style={{ flex: 2 }}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                            <span style={{
                              width: '16px', height: '16px',
                              border: '2px solid transparent', borderTop: '2px solid white',
                              borderRadius: '50%', animation: 'spin 1s linear infinite',
                              display: 'inline-block'
                            }} />
                            Creating Account...
                          </span>
                        ) : (
                          'Create Provider Account'
                        )}
                      </button>
                    </div>
                  </fieldset>
                )}
              </form>
            </div>

            {/* Footer Links */}
            <div className="text-center" style={{ marginTop: '2rem' }}>
              <p style={{ color: '#d1d5db', fontSize: '0.875rem' }}>
                Already have an account?{' '}
                <Link to="/provider-login" style={{ color: '#D4A017', textDecoration: 'none' }}>
                  Sign In
                </Link>
              </p>
              <p style={{ color: '#d1d5db', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                Looking for patient access?{' '}
                <Link to="/client-login" style={{ color: '#D4A017', textDecoration: 'none' }}>
                  Client Portal
                </Link>
              </p>
              <Link
                to="/"
                style={{
                  color: '#d1d5db', textDecoration: 'none', fontSize: '0.875rem',
                  display: 'inline-block', marginTop: '0.5rem'
                }}
              >
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </main>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ProviderSignup;
